import { 
  Container, 
  Typography, 
  Grid, 
  Box,
  Divider,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import EmailIcon from '@mui/icons-material/Email';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useState, useEffect } from 'react';
import { boardMemberService } from '../services/api';

const BoardMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fallback members in case API fails
  const fallbackMembers = [
    {
      name: "Kassandra Larson",
      position: "President",
      email: "president@brynleighestates.com",
      bio: "A Colorado native, I moved here with my partner Ben nine years ago. We share our home with our two dogs, Aite and Julius. I've worked in Early Childhood Education for the past 17 years."
    },
    {
      name: "Kelly Moore",
      position: "Vice President",
      email: "vp@brynleighestates.com",
      bio: "[Bio coming soon.]"
    },
    {
      name: "Daniel E. Hermony Jr.",
      position: "Treasurer",
      email: "treasurer@brynleighestates.com",
      bio: "Married to Sandy for nearly 19 years, and together we have five wonderful kids. I'm a retired veteran with 23 years of service and now run an independent insurance brokerage."
    },
    {
      name: "Josh Holtzclaw",
      position: "Technology Guy",
      email: "tech@brynleighestates.com",
      bio: "Married to Laura for seven years, and we're raising three awesome kids under seven. I'm a full-stack web developer/marketer (18 years) and co-owner of OP Solutions. I also write and record music in my spare time."
    },
    {
      name: "Matt Smith",
      position: "ARC Chair",
      email: "arc@brynleighestates.com",
      bio: "ARC Committee Chair"
    },
    {
      name: "Coty",
      position: "ARC Member",
      email: "arc@brynleighestates.com",
      bio: "ARC Committee Member"
    }
  ];

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        
        const response = await boardMemberService.getAll({
          populate: '*',
          'filters[active][$eq]': true,
          sort: 'order:asc'
        });
        
        // Handle response from API - direct array instead of nested data structure
        if (response && response.data) {
          let memberData = [];
          
          // Check if we have direct array or nested data structure
          if (Array.isArray(response.data)) {
            memberData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            memberData = response.data.data;
          }
          
          if (memberData.length > 0) {
            const processedMembers = memberData.map(member => {
              // Use direct properties instead of attributes
              return {
                id: member.id,
                name: member.name || 'Member',
                position: member.position || 'Member',
                email: member.email || 'info@brynleighestates.com',
                bio: member.bio || "Member information coming soon."
              };
            });
            
            setMembers(processedMembers);
          } else {
            console.warn('No member data found, using fallback');
            setMembers(fallbackMembers);
          }
        } else {
          console.warn('Invalid API response format:', response);
          setMembers(fallbackMembers);
        }
      } catch (error) {
        console.error('Error fetching members:', error);
        setError('Failed to load leadership data. Using fallback data.');
        setMembers(fallbackMembers);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Board & Committee Members | Brynleigh Estates HOA</title>
        <meta name="description" content="Meet the dedicated board and committee members who volunteer their time to serve the Brynleigh Estates community in Chelsea, Alabama." />
        <link rel="canonical" href="https://brynleighestates.com/board-members" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/board-members" />
        <meta property="og:title" content="Board & Committee Members | Brynleigh Estates HOA" />
        <meta property="og:description" content="Meet the dedicated board and committee members who volunteer their time to serve the Brynleigh Estates community in Chelsea, Alabama." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Board & Committee Members | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Meet the dedicated board and committee members who volunteer their time to serve the Brynleigh Estates community." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      
      <Hero 
        imageUrl="/images/hills-bg.jpeg"
        title="Board & Committee Members"
        subtitle="Meet the team serving our community"
      />
      
      <Container maxWidth="lg">
        {error && (
          <Box sx={{ my: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography color="error.dark">{error}</Typography>
          </Box>
        )}
        
        <Box sx={{ my: 6 }}>
          <Typography variant="h4" gutterBottom fontWeight="medium" component="h1" align="center">
            Community Leadership
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ mb: 4 }}>
            Our volunteer board members and committee members work diligently to maintain and improve our community
          </Typography>
          <Divider sx={{ mb: 4 }} />
          
          <Grid container spacing={3}>
            {members.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={member.id || index}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 2
                    }
                  }}
                >
                  <CardHeader
                    title={
                      <Typography variant="h6">{member.name}</Typography>
                    }
                    subheader={
                      <Chip 
                        label={member.position} 
                        size="small" 
                        color="primary" 
                        sx={{ mt: 0.5 }}
                      />
                    }
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {member.bio}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <EmailIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {member.email}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Paper 
            elevation={0}
            sx={{ 
              p: 4, 
              mt: 6,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <HandshakeIcon color="primary" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Get Involved
                </Typography>
                <Typography variant="body1" paragraph>
                  If you're interested in joining a committee or running for the board, please contact the board president.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default BoardMembers; 